import Sheet from '../../shared/ui/Sheet';
import { Card, EmptyState } from '../../shared/ui';
import { CheckCircle2, Inbox, User, MapPin, ShoppingBag, Clock, StickyNote, Eye, Footprints } from 'lucide-react';
import { driverPayoutFor, formatCurrency, formatTime, distanceMiles, orderPickupParish, normalizeParish } from '../../shared/models';
import { deliveryAddressText, restaurantCoords, customerCoords } from './geo';

// One available-order card (mirrors iOS AvailableOrderCard).
function AvailableOrderCard({ order, onPreview }) {
  const payout = driverPayoutFor(order);
  const num = String(order.orderNumber || order.id).slice(0, 8);
  const itemCount = (order.orderItems || order.items || []).length;
  const address = deliveryAddressText(order);
  const miles = distanceMiles(restaurantCoords(order), customerCoords(order));
  const walkable = miles != null && miles <= 1.0;
  const pickupParish = orderPickupParish(order);
  const dropParish = normalizeParish(order.deliveryAddress?.parish || order.raw?.deliveryAddress?.parish);
  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-extrabold text-gray-900">Order #{num}</p>
          <p className="truncate text-xs text-gray-500">{order.restaurantName || 'Restaurant'}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-sm font-extrabold text-green-600">
            {formatCurrency(payout)}
          </p>
          <p className="text-[10px] text-gray-400">Driver Payout</p>
        </div>
      </div>

      <div className="space-y-1.5">
        {order.customerName && (
          <div className="flex items-center gap-2 text-xs text-gray-700">
            <User size={14} className="shrink-0 text-gray-400" />
            <span className="truncate">{order.customerName}</span>
          </div>
        )}
        {address && (
          <div className="flex items-start gap-2 text-[11px] text-gray-500">
            <MapPin size={14} className="mt-0.5 shrink-0 text-gray-400" />
            <span className="line-clamp-2">{address}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <ShoppingBag size={14} className="shrink-0 text-gray-400" />
          <span>{itemCount} item{itemCount === 1 ? '' : 's'}</span>
          {miles != null && (
            <span className="flex items-center gap-1">
              <Footprints size={13} className="text-gray-400" />
              {miles.toFixed(1)} mi
            </span>
          )}
          {walkable && (
            <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-bold text-green-700">
              Walkable
            </span>
          )}
          {pickupParish && (
            <span className="flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-700">
              <MapPin size={10} />
              {pickupParish}
            </span>
          )}
          <span className="flex-1" />
          {order.estimatedDeliveryTime && (
            <span className="flex items-center gap-1">
              <Clock size={13} className="text-gray-400" />
              Due: {formatTime(order.estimatedDeliveryTime)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <span className="w-[14px]" />
          <span>Delivery fee {formatCurrency(order.deliveryFee)}</span>
          {dropParish && dropParish !== pickupParish && (
            <span className="text-gray-400">→ drop-off {dropParish}</span>
          )}
        </div>
      </div>

      {order.specialInstructions ? (
        <div className="flex items-start gap-2 text-[11px] text-[#FF6B35]">
          <StickyNote size={14} className="mt-0.5 shrink-0" />
          <span className="line-clamp-2">{order.specialInstructions}</span>
        </div>
      ) : null}

      <button
        onClick={() => onPreview(order)}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-500 py-2.5 text-sm font-semibold text-white active:scale-[0.98]"
      >
        <Eye size={16} />
        View Details
      </button>
    </Card>
  );
}

// Bottom sheet listing ready orders (mirrors DriverOrdersView sheet).
export default function AvailableOrdersSheet({ open, onClose, orders, atCapacity, onPreview }) {
  const sorted = [...orders].sort(
    (a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0)
  );
  return (
    <Sheet open={open} onClose={onClose} title="Available Orders">
      {atCapacity ? (
        <EmptyState
          icon={CheckCircle2}
          title="Deliveries Full"
          subtitle="You have 2 active deliveries. Complete one before accepting more."
        />
      ) : sorted.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No Available Orders"
          subtitle="Check back later for new delivery opportunities"
        />
      ) : (
        <div className="space-y-3 p-4">
          {sorted.map((o) => (
            <AvailableOrderCard key={o.id} order={o} onPreview={onPreview} />
          ))}
        </div>
      )}
    </Sheet>
  );
}
